/**
 * Super-admin tenant detail — drill-in from the super-admin dashboard.
 *
 * Tabbed layout so super-admin can see everything that tenant's admin sees:
 *   • Tenant information — KPIs, consultancy contact, members
 *   • Boards            — projects + issue counts (cross-tenant /projects)
 *
 * A tenant switcher in the header lets them pivot between tenants without
 * navigating back to the dashboard list. Read-only by design.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type CSSProperties } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Avatar } from "@m2s/design-system";

import { ProfileDrawer } from "@/components/ProfileDrawer";
import { api } from "@/lib/api";
import type { AdminTeamTimesheet, Member } from "@/lib/types";
import { useAuthStore } from "@/store/auth";

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function fmtISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtMonthLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}
function startOfWeek(d: Date): Date {
  const day = d.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + mondayOffset);
}
function endOfWeek(d: Date): Date {
  const s = startOfWeek(d);
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6);
}
function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}
function endOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 11, 31);
}
function parseISO(s: string): Date {
  return new Date(`${s}T00:00:00`);
}
function fmtRangeLabel(from: Date, to: Date): string {
  const fmt: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  return `${from.toLocaleDateString(undefined, fmt)} – ${to.toLocaleDateString(undefined, fmt)} ${to.getFullYear()}`;
}
type Granularity = "week" | "month" | "year" | "custom";
function stepAnchor(d: Date, g: Granularity, dir: number): Date {
  if (g === "week") return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7 * dir);
  if (g === "year") return new Date(d.getFullYear() + dir, d.getMonth(), 1);
  return new Date(d.getFullYear(), d.getMonth() + dir, 1);
}
function hms(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}
function hoursStr(seconds: number): string {
  if (!seconds) return "0";
  return (seconds / 3600).toFixed(2);
}

type TenantSummary = {
  id: string;
  slug: string;
  name: string;
  plan: string;
  created_at: string;
  phone: string | null;
  mailing_address: string | null;
  billing_email: string | null;
  member_count: number;
  project_count: number;
  issue_count: number;
  open_issue_count: number;
  last_activity_at: string | null;
};

type TenantList = {
  tenants: TenantSummary[];
  total_tenants: number;
  total_members: number;
  total_open_issues: number;
};

type TenantProject = {
  id: string;
  key: string;
  name: string;
  color: string;
  issue_count: number;
  open_count: number;
  done_count: number;
  points_total: number;
  points_done: number;
};

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  employee: "Employee",
  owner: "Admin",
  project_lead: "Admin",
  member: "Employee",
  viewer: "Employee",
};

type Tab = "info" | "boards" | "admin";

export function SuperAdminTenantDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const [tab, setTab] = useState<Tab>("info");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string>("");

  // Tenant list for the switcher dropdown.
  const { data: tenantList } = useQuery({
    queryKey: ["superadmin-tenants"],
    queryFn: async () => (await api.get<TenantList>("/superadmin/tenants")).data,
    enabled: isSuperAdmin,
  });

  const { data: tenant, isLoading: tLoading } = useQuery({
    queryKey: ["superadmin-tenant", slug],
    queryFn: async () =>
      (await api.get<TenantSummary>(`/superadmin/tenants/${slug}`)).data,
    enabled: Boolean(slug) && isSuperAdmin,
  });

  const { data: members = [], isLoading: mLoading } = useQuery({
    queryKey: ["superadmin-tenant-members", slug],
    queryFn: async () =>
      (await api.get<Member[]>(`/superadmin/tenants/${slug}/members`)).data,
    enabled: Boolean(slug) && isSuperAdmin,
  });

  const { data: projects = [], isLoading: pLoading } = useQuery({
    queryKey: ["superadmin-tenant-projects", slug],
    queryFn: async () =>
      (await api.get<TenantProject[]>(`/superadmin/tenants/${slug}/projects`)).data,
    enabled: Boolean(slug) && isSuperAdmin && tab === "boards",
  });

  // Admin-dashboard tab — same matrix the tenant admin sees on /admin,
  // with the same Week / Month / Year / Custom range filter.
  const [granularity, setGranularity] = useState<Granularity>("month");
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [customFrom, setCustomFrom] = useState<string>(() => fmtISO(startOfMonth(new Date())));
  const [customTo, setCustomTo] = useState<string>(() => fmtISO(endOfMonth(new Date())));
  const { from, to } = useMemo(() => {
    if (granularity === "week") return { from: startOfWeek(anchor), to: endOfWeek(anchor) };
    if (granularity === "year") return { from: startOfYear(anchor), to: endOfYear(anchor) };
    if (granularity === "custom") return { from: parseISO(customFrom), to: parseISO(customTo) };
    return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
  }, [granularity, anchor, customFrom, customTo]);
  const fromISO = fmtISO(from);
  const toISO = fmtISO(to);

  const { data: team, isLoading: aLoading } = useQuery({
    queryKey: ["superadmin-tenant-team-timesheet", slug, fromISO, toISO],
    queryFn: async () =>
      (
        await api.get<AdminTeamTimesheet>(
          `/superadmin/tenants/${slug}/team-timesheet?from=${fromISO}&to=${toISO}`,
        )
      ).data,
    enabled: Boolean(slug) && isSuperAdmin && tab === "admin",
  });

  const cellByKey = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of team?.cells ?? []) {
      m.set(`${c.user_id}::${c.project_id}`, c.seconds);
    }
    return m;
  }, [team]);

  if (!isSuperAdmin) {
    return (
      <div>
        <h1 style={{ fontSize: 28, fontWeight: 600 }}>Tenant detail</h1>
        <p style={{ color: "var(--mute)", marginTop: 8 }}>Super-admin only.</p>
      </div>
    );
  }

  return (
    <div>
      <header style={{ marginBottom: 16 }}>
        <Link to="/superadmin/dashboard" style={backLink}>
          ‹ All tenants
        </Link>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            marginTop: 8,
            flexWrap: "wrap",
          }}
        >
          <h1
            style={{
              fontSize: 28,
              fontWeight: 600,
              letterSpacing: "-0.3px",
              margin: 0,
              flex: 1,
              minWidth: 0,
            }}
          >
            {tLoading ? "…" : tenant?.name ?? "Tenant"}
          </h1>

          {tenantList && tenantList.tenants.length > 1 && (
            <div style={switcherWrap}>
              <span style={switcherLabel}>Switch to</span>
              <select
                value={slug ?? ""}
                onChange={(e) =>
                  navigate(`/superadmin/tenants/${e.target.value}`)
                }
                style={switcherSelect}
              >
                {tenantList.tenants.map((t) => (
                  <option key={t.id} value={t.slug}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {tenant && (
          <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
            <span style={slugMono}>{tenant.slug}</span>
            <span style={{ marginLeft: 12 }}>Plan: {tenant.plan}</span>
            <span style={{ marginLeft: 12 }}>
              Created {new Date(tenant.created_at).toLocaleDateString()}
            </span>
          </p>
        )}
      </header>

      {/* Tabs */}
      <div style={tabBar}>
        <button
          type="button"
          onClick={() => setTab("info")}
          style={tabBtn(tab === "info")}
        >
          Tenant information
        </button>
        <button
          type="button"
          onClick={() => setTab("boards")}
          style={tabBtn(tab === "boards")}
        >
          Boards
        </button>
        <button
          type="button"
          onClick={() => setTab("admin")}
          style={tabBtn(tab === "admin")}
        >
          Admin dashboard
        </button>
      </div>

      {tab === "info" && (
        <>
          {/* KPI cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 12,
              marginBottom: 24,
            }}
          >
            <Kpi
              label="Members"
              value={tenant?.member_count ?? 0}
              accent="var(--brand-teal, #0F766E)"
            />
            <Kpi
              label="Projects"
              value={tenant?.project_count ?? 0}
              accent="var(--accent-purple, #7a3dff)"
            />
            <Kpi
              label="Open issues"
              value={tenant?.open_issue_count ?? 0}
              accent="var(--accent-orange, #ff6b00)"
            />
            <Kpi
              label="Total issues"
              value={tenant?.issue_count ?? 0}
              accent="var(--accent-blue, #3b89ff)"
            />
          </div>

          {/* Contact info */}
          <section style={card}>
            <div style={sectionTitle}>Consultancy contact</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
              <Field label="Phone" value={tenant?.phone} />
              <Field label="Billing email" value={tenant?.billing_email} />
              <Field
                label="Mailing address"
                value={tenant?.mailing_address}
                multiline
              />
            </div>
          </section>

          {/* Members */}
          <section style={{ marginTop: 24 }}>
            <div style={sectionTitle}>
              Members ·{" "}
              {mLoading
                ? "…"
                : `${members.filter((m) => m.status !== "removed").length} active`}
              {!mLoading && members.some((m) => m.status === "removed")
                ? ` · ${members.filter((m) => m.status === "removed").length} removed`
                : ""}
            </div>
            {mLoading ? (
              <p style={{ color: "var(--mute)" }}>Loading…</p>
            ) : members.length === 0 ? (
              <p style={{ color: "var(--mute)" }}>No members yet.</p>
            ) : (
              <div
                style={{
                  border: "1px solid var(--hairline)",
                  borderRadius: 8,
                  background: "var(--canvas)",
                  overflowX: "auto",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "var(--surface-1)" }}>
                      <th style={th}>Member</th>
                      <th style={th}>Email</th>
                      <th style={th}>Role</th>
                      <th style={th}>Profile</th>
                      <th style={th}>Status</th>
                      <th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => (
                      <tr
                        key={m.user_id}
                        style={{
                          borderTop: "1px solid var(--hairline)",
                          // Removed members stay visible (super-admin audit) but
                          // de-emphasised so active members read first.
                          opacity: m.status === "removed" ? 0.55 : 1,
                          background:
                            m.status === "removed" ? "var(--surface-1)" : undefined,
                        }}
                      >
                        <td style={td}>
                          <span
                            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                          >
                            <span
                              title={
                                m.github_connected
                                  ? "GitHub PAT connected — Atlas-ready"
                                  : "GitHub PAT not connected yet"
                              }
                              aria-label={
                                m.github_connected ? "PAT connected" : "PAT not connected"
                              }
                              style={{
                                color: m.github_connected ? "#12a150" : "#e08600",
                                fontSize: 15,
                                lineHeight: 1,
                                width: 14,
                                textAlign: "center",
                              }}
                            >
                              ★
                            </span>
                            <Avatar
                              initials={initialsOf(m.display_name)}
                              title={m.display_name}
                            />
                            <span>{m.display_name}</span>
                          </span>
                        </td>
                        <td
                          style={{
                            ...td,
                            color: "var(--body-mid)",
                            fontFamily: "var(--font-mono)",
                            fontSize: 12,
                          }}
                        >
                          {m.email}
                        </td>
                        <td style={td}>
                          <span style={rolePill}>{ROLE_LABELS[m.role] ?? m.role}</span>
                        </td>
                        <td style={td}>
                          <span style={profilePill(m.profile_complete)}>
                            {m.profile_complete ? "Complete" : "Pending"}
                          </span>
                        </td>
                        <td style={td}>
                          <span style={statusBadge(m.status)}>
                            {m.status === "removed" ? "Removed" : m.status}
                          </span>
                        </td>
                        <td style={{ ...td, textAlign: "right" }}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedUserId(m.user_id);
                              setSelectedName(m.display_name);
                            }}
                            style={viewBtn}
                          >
                            View profile
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {tab === "boards" && (
        <section>
          <div style={sectionTitle}>
            Boards · {pLoading ? "…" : projects.length}
          </div>
          {pLoading ? (
            <p style={{ color: "var(--mute)" }}>Loading…</p>
          ) : projects.length === 0 ? (
            <p style={{ color: "var(--mute)" }}>
              No projects yet for this tenant.
            </p>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {projects.map((p) => (
                <div key={p.id} style={boardCard}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span
                      aria-hidden
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        background: p.color,
                        flexShrink: 0,
                      }}
                    />
                    <strong style={{ fontSize: 15, color: "var(--ink)" }}>
                      {p.name}
                    </strong>
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        background: "var(--surface-2)",
                        padding: "1px 6px",
                        borderRadius: 3,
                        color: "var(--body-mid)",
                      }}
                    >
                      {p.key}
                    </span>
                  </div>
                  <div style={boardStats}>
                    <Stat label="Issues" value={p.issue_count} />
                    <Stat label="Open" value={p.open_count} accent="#ff6b00" />
                    <Stat label="Done" value={p.done_count} accent="#047a18" />
                    <Stat
                      label="Points"
                      value={`${p.points_done} / ${p.points_total}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "admin" && (
        <section>
          {/* Range filter — Week / Month / Year / Custom */}
          <div style={monthBar}>
            <div role="group" aria-label="Granularity" style={segmented}>
              {(["week", "month", "year", "custom"] as Granularity[]).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGranularity(g)}
                  aria-pressed={granularity === g}
                  style={segmentBtn(granularity === g)}
                >
                  {g[0].toUpperCase() + g.slice(1)}
                </button>
              ))}
            </div>
            {granularity === "custom" ? (
              <>
                <span style={fieldHint}>From</span>
                <input
                  type="date"
                  value={customFrom}
                  max={customTo}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  style={dateInput}
                  aria-label="Range start"
                />
                <span style={fieldHint}>To</span>
                <input
                  type="date"
                  value={customTo}
                  min={customFrom}
                  onChange={(e) => setCustomTo(e.target.value)}
                  style={dateInput}
                  aria-label="Range end"
                />
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setAnchor((d) => stepAnchor(d, granularity, -1))}
                  style={navBtn}
                  aria-label="Previous period"
                >
                  ‹
                </button>
                <span style={monthLabel}>
                  {granularity === "month"
                    ? fmtMonthLabel(anchor)
                    : granularity === "year"
                      ? String(anchor.getFullYear())
                      : fmtRangeLabel(from, to)}
                </span>
                <button
                  type="button"
                  onClick={() => setAnchor((d) => stepAnchor(d, granularity, 1))}
                  style={navBtn}
                  aria-label="Next period"
                >
                  ›
                </button>
                <button type="button" onClick={() => setAnchor(new Date())} style={navBtn}>
                  Today
                </button>
              </>
            )}
          </div>

          {aLoading ? (
            <p style={{ color: "var(--mute)" }}>Loading…</p>
          ) : !team ? (
            <p style={{ color: "var(--mute)" }}>No data.</p>
          ) : (
            <>
              {/* Headline metrics */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 12,
                  marginBottom: 24,
                }}
              >
                <Kpi
                  label="Total logged"
                  value={hms(team.grand_total_seconds)}
                  accent="var(--ink)"
                />
                <Kpi
                  label="Billable"
                  value={hms(team.grand_billable_seconds)}
                  accent="var(--brand-teal, #0F766E)"
                />
                <Kpi
                  label="Non-billable"
                  value={hms(team.grand_total_seconds - team.grand_billable_seconds)}
                  accent="var(--mute)"
                />
                <Kpi
                  label="Active users"
                  value={team.users.length}
                  accent="var(--accent-purple, #7a3dff)"
                />
              </div>

              {/* By-project */}
              {team.projects.length > 0 && (
                <section style={{ marginBottom: 24 }}>
                  <div style={sectionTitle}>By project</div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gap: 12,
                    }}
                  >
                    {team.projects.map((p) => {
                      const pct = team.grand_total_seconds
                        ? (p.total_seconds / team.grand_total_seconds) * 100
                        : 0;
                      return (
                        <div
                          key={p.project_id}
                          style={{
                            background: "var(--canvas)",
                            border: "1px solid var(--hairline)",
                            borderRadius: 8,
                            padding: 16,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              marginBottom: 8,
                            }}
                          >
                            <span
                              aria-hidden
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: 2,
                                background: p.color,
                              }}
                            />
                            <strong style={{ fontSize: 14, color: "var(--ink)" }}>
                              {p.name}
                            </strong>
                            <span
                              style={{
                                fontFamily: "var(--font-mono)",
                                fontSize: 10.5,
                                color: "var(--mute)",
                                marginLeft: "auto",
                              }}
                            >
                              {p.key}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: 22,
                              fontWeight: 600,
                              letterSpacing: "-0.3px",
                            }}
                          >
                            {hms(p.total_seconds)}
                          </div>
                          <div style={{ fontSize: 11.5, color: "var(--mute)", marginTop: 4 }}>
                            {hms(p.billable_seconds)} billable · {pct.toFixed(0)}% of total
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* People × projects matrix */}
              <section>
                <div style={sectionTitle}>People × projects</div>
                <div
                  style={{
                    border: "1px solid var(--hairline)",
                    borderRadius: 8,
                    background: "var(--canvas)",
                    overflowX: "auto",
                  }}
                >
                  <table
                    style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
                  >
                    <thead>
                      <tr style={{ background: "var(--surface-1)" }}>
                        <th style={th}>Member</th>
                        <th style={th}>Role</th>
                        {team.projects.map((p) => (
                          <th
                            key={p.project_id}
                            style={{
                              ...th,
                              textAlign: "right",
                              fontFamily: "var(--font-mono)",
                            }}
                          >
                            <span
                              aria-hidden
                              style={{
                                display: "inline-block",
                                width: 6,
                                height: 6,
                                borderRadius: 2,
                                background: p.color,
                                marginRight: 6,
                                verticalAlign: "middle",
                              }}
                            />
                            {p.key}
                          </th>
                        ))}
                        <th style={{ ...th, textAlign: "right" }}>Total</th>
                        <th style={{ ...th, textAlign: "right" }}>Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {team.users.map((u) => (
                        <tr
                          key={u.user_id}
                          style={{ borderTop: "1px solid var(--hairline)" }}
                        >
                          <td style={td}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              <Avatar
                                initials={initialsOf(u.display_name)}
                                title={u.display_name}
                              />
                              <div>
                                <div>{u.display_name}</div>
                                <div
                                  style={{
                                    fontSize: 11.5,
                                    color: "var(--mute)",
                                    fontFamily: "var(--font-mono)",
                                  }}
                                >
                                  {u.email}
                                </div>
                              </div>
                            </span>
                          </td>
                          <td style={td}>
                            <span style={rolePill}>{ROLE_LABELS[u.role] ?? u.role}</span>
                          </td>
                          {team.projects.map((p) => {
                            const sec = cellByKey.get(
                              `${u.user_id}::${p.project_id}`,
                            );
                            return (
                              <td
                                key={p.project_id}
                                style={{
                                  ...td,
                                  textAlign: "right",
                                  fontFamily: "var(--font-mono)",
                                  fontSize: 12.5,
                                  color: sec ? "var(--ink)" : "var(--mute)",
                                }}
                              >
                                {sec ? hoursStr(sec) : "0"}
                              </td>
                            );
                          })}
                          <td
                            style={{
                              ...td,
                              textAlign: "right",
                              fontFamily: "var(--font-mono)",
                              fontWeight: 600,
                            }}
                          >
                            {hoursStr(u.total_seconds)}
                          </td>
                          <td
                            style={{
                              ...td,
                              textAlign: "right",
                              fontFamily: "var(--font-mono)",
                              color: "var(--body-mid)",
                            }}
                          >
                            {u.days_logged}
                          </td>
                        </tr>
                      ))}
                      {team.users.length > 0 && (
                        <tr
                          style={{
                            background: "var(--surface-1)",
                            fontWeight: 600,
                          }}
                        >
                          <td style={td}>Total</td>
                          <td style={td} />
                          {team.projects.map((p) => (
                            <td
                              key={p.project_id}
                              style={{
                                ...td,
                                textAlign: "right",
                                fontFamily: "var(--font-mono)",
                              }}
                            >
                              {hoursStr(p.total_seconds)}
                            </td>
                          ))}
                          <td
                            style={{
                              ...td,
                              textAlign: "right",
                              fontFamily: "var(--font-mono)",
                            }}
                          >
                            {hoursStr(team.grand_total_seconds)}
                          </td>
                          <td style={td} />
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </section>
      )}

      {selectedUserId && (
        <ProfileDrawer
          userId={selectedUserId}
          displayName={selectedName}
          mode="superadmin"
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
}

// ---------- helpers ----------

function Kpi({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent: string;
}) {
  return (
    <div
      style={{
        background: "var(--canvas)",
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        padding: 16,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: accent,
          opacity: 0.85,
        }}
      />
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: "var(--mute)",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.4px" }}>
        {value}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: string;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 500,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: "var(--mute)",
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 600,
          fontFamily: "var(--font-mono)",
          color: accent ?? "var(--ink)",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string | null | undefined;
  multiline?: boolean;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          color: "var(--mute)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          color: value ? "var(--ink)" : "var(--mute)",
          whiteSpace: multiline ? "pre-wrap" : undefined,
        }}
      >
        {value ?? "—"}
      </div>
    </div>
  );
}

function initialsOf(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ---------- styles ----------

const backLink: CSSProperties = {
  fontSize: 12,
  color: "var(--mute)",
  textDecoration: "none",
};

const slugMono: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  background: "var(--surface-2)",
  padding: "2px 8px",
  borderRadius: 3,
  color: "var(--body-mid)",
};

const switcherWrap: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 10px",
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
};

const switcherLabel: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
};

const switcherSelect: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "4px 8px",
  fontSize: 13,
  color: "var(--ink)",
  fontFamily: "var(--font-sans)",
  cursor: "pointer",
};

const tabBar: CSSProperties = {
  display: "flex",
  gap: 4,
  borderBottom: "1px solid var(--hairline)",
  marginBottom: 24,
};

function tabBtn(active: boolean): CSSProperties {
  return {
    background: "transparent",
    border: "none",
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: active ? 600 : 500,
    color: active ? "var(--ink)" : "var(--body-mid)",
    cursor: "pointer",
    borderBottom: active ? "2px solid var(--ink)" : "2px solid transparent",
    marginBottom: -1,
  };
}

const card: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 20,
  marginBottom: 16,
};

const sectionTitle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 12,
};

const th: CSSProperties = {
  textAlign: "left",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  padding: "10px 12px",
  borderBottom: "1px solid var(--hairline)",
};

const td: CSSProperties = {
  padding: "10px 12px",
  fontSize: 13,
  color: "var(--body)",
  verticalAlign: "middle",
};

const rolePill: CSSProperties = {
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 2,
  background: "var(--surface-2)",
  color: "var(--body-mid)",
};

const viewBtn: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "4px 10px",
  fontSize: 12,
  cursor: "pointer",
  color: "var(--ink)",
};

const boardCard: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 16,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 24,
};

const boardStats: CSSProperties = {
  display: "flex",
  gap: 24,
};

const monthBar: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 20,
};

const monthLabel: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "5px 14px",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  fontFamily: "var(--font-mono)",
  color: "var(--ink)",
};

const navBtn: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "5px 10px",
  fontSize: 12,
  color: "var(--ink)",
  cursor: "pointer",
};

const segmented: CSSProperties = {
  display: "inline-flex",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  overflow: "hidden",
};

function segmentBtn(active: boolean): CSSProperties {
  return {
    background: active ? "var(--ink)" : "var(--canvas)",
    color: active ? "var(--on-primary)" : "var(--ink)",
    border: "none",
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: active ? 600 : 400,
    cursor: "pointer",
  };
}

const dateInput: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "4px 8px",
  fontSize: 12.5,
  fontFamily: "var(--font-mono)",
  color: "var(--ink)",
};

const fieldHint: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
};

function profilePill(complete: boolean): CSSProperties {
  return {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 2,
    background: complete ? "rgba(0,215,34,0.12)" : "rgba(255,174,19,0.14)",
    color: complete ? "#047a18" : "#8a5b00",
  };
}

function statusBadge(status: string): CSSProperties {
  const removed = status === "removed";
  return {
    fontSize: 11,
    fontWeight: 500,
    padding: "2px 8px",
    borderRadius: 2,
    textTransform: "capitalize",
    background: removed ? "rgba(238,29,54,0.10)" : "rgba(0,215,34,0.12)",
    color: removed ? "#c11930" : "#047a18",
  };
}
